import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Product } from '../models/admin.models';
import { environment } from '../../environments/environment';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.backendUrl}/products`;

  private cache = new Map<string, CacheEntry<any>>();
  private inflight = new Map<string, Observable<any>>();

  getProducts(search?: string, category?: string): Observable<Product[]> {
    const params: string[] = [];
    if (search) params.push(`q=${encodeURIComponent(search)}`);
    if (category) params.push(`category=${encodeURIComponent(category)}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${this.apiUrl}${query}`;
    const key = `list:${query}`;
    return this.getCached<Product[]>(key, url);
  }

  getProductById(id: string): Observable<Product> {
    const url = `${this.apiUrl}/${id}`;
    const key = `detail:${id}`;
    return this.getCached<Product>(key, url);
  }

  /** Invalidate all cached product data (call after admin mutations). */
  clearCache(): void {
    this.cache.clear();
    this.inflight.clear();
  }

  private getCached<T>(key: string, url: string): Observable<T> {
    // Return unexpired cached value immediately
    const entry = this.cache.get(key);
    if (entry && Date.now() < entry.expiresAt) {
      return of(entry.data as T);
    }

    // Return an already in-flight request if one exists
    if (this.inflight.has(key)) {
      return this.inflight.get(key)!;
    }

    // Make a new request, cache the result, and share among concurrent subscribers
    const request$ = this.http.get<T>(url).pipe(
      tap(data => {
        this.cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
        this.inflight.delete(key);
      }),
      shareReplay(1)
    );

    this.inflight.set(key, request$);
    return request$;
  }
}
